// src/api/projectApi.js

import axios from 'axios';

const BASE_URL = "https://ims.holdthechild.net/api";

const getAuthHeaders = () => {
  const token = localStorage.getItem('token');
  return {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${token}`,
  };
};

export const fetchProjects = async () => {
  try {
    const token = localStorage.getItem('token');
    const response = await axios.get(`${BASE_URL}/projects`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return response.data.data;
  } catch (error) {
    console.error('Failed to fetch projects:', error);
    throw error;
  }
};

export const fetchProjectById = async (id) => {
  const res = await axios.get(`${BASE_URL}/projects/${id}`, {
    headers: getAuthHeaders(),
  });
  return res.data.data;
};

export const createProject = async (projectData) => {
  const res = await axios.post(`${BASE_URL}/projects`, projectData, {
    headers: getAuthHeaders(),
  });
  return res.data.data;
};

export const fetchDonors = async () => {
  const res = await axios.get(`${BASE_URL}/donors`, {
    headers: getAuthHeaders(),
  });
  return res.data.data;
};

export const fetchRequirementsByProjectId = async (projectId) => {
  try {
    const response = await axios.get(`${BASE_URL}/requirements/${projectId}`, {
      headers: getAuthHeaders(),
    });
    return response.data; // Requirements endpoint returns array directly
  } catch (error) {
    console.error('Failed to fetch requirements:', error);
    throw error;
  }
};

export const createRequirement = async (requirementData) => {
  try {
    const response = await axios.post(`${BASE_URL}/requirements`, requirementData, {
      headers: getAuthHeaders(),
    });
    return response.data; // Returns the created requirement
  } catch (error) {
    console.error('Failed to create requirement:', error);
    throw error;
  }
};

export const fetchProjectPortfolioStatus = async () => {
  try {
    const response = await axios.get(`${BASE_URL}/projects/stats/portfolio`, {
      headers: getAuthHeaders(),
    });
    return response.data.data; // Returns the portfolio status data
  } catch (error) {
    console.error('Failed to fetch project portfolio status:', error);
    throw error;
  }
};

export const fetchProcurementPlans = async () => {
  try {
    const response = await axios.get(`${BASE_URL}/procurement-plans`, {
      headers: getAuthHeaders(),
    });
    return response.data.data; // Returns array of procurement plans
  } catch (error) {
    console.error('Error fetching procurement plans:', error);
    throw error;
  }
};

// Fetch Planning projects without procurement plan
export const fetchPlanningProjectsWithoutProcurementPlan = async () => {
  try {
    const response = await axios.get(
      `${BASE_URL}/projects/planning/no-procurement-plan`,
      { headers: getAuthHeaders() }
    );
    return response.data.data; // Returns the list of projects
  } catch (error) {
    console.error('Failed to fetch Planning projects without procurement plan:', error);
    throw error;
  }
};

// ==================== COMPLIANCE API FUNCTIONS (NEW) ====================

// Get all projects with compliance data
export const fetchProjectsWithCompliance = async () => {
  try {
    const response = await axios.get(`${BASE_URL}/compliance/summary`, {
      headers: getAuthHeaders(),
    });
    return response.data.data;
  } catch (error) {
    console.error('Failed to fetch projects with compliance data:', error);
    throw error;
  }
};

// Get compliance metrics for dashboard
export const getComplianceMetrics = async () => {
  try {
    const response = await axios.get(`${BASE_URL}/compliance/metrics`, {
      headers: getAuthHeaders(),
    });
    return response.data.data;
  } catch (error) {
    console.error('Failed to fetch compliance metrics:', error);
    throw error;
  }
};

// Get compliance for a specific project
export const getProjectCompliance = async (projectId) => {
  try {
    const response = await axios.get(`${BASE_URL}/compliance/projects/${projectId}`, {
      headers: getAuthHeaders(),
    });
    return response.data.data;
  } catch (error) {
    console.error('Failed to fetch project compliance:', error);
    throw error;
  }
};

// Toggle compliance status for an item
export const toggleComplianceItem = async (projectId, itemId) => {
  try {
    const response = await axios.put(`${BASE_URL}/compliance/projects/${projectId}/items/${itemId}/toggle`, {}, {
      headers: getAuthHeaders(),
    });
    return response.data.data;
  } catch (error) {
    console.error('Failed to toggle compliance item:', error);
    throw error;
  }
};

// Add a custom compliance item
export const addComplianceItem = async (projectId, itemData) => {
  try {
    const response = await axios.post(`${BASE_URL}/compliance/projects/${projectId}/items`, itemData, {
      headers: getAuthHeaders(),
    });
    return response.data.data;
  } catch (error) {
    console.error('Failed to add compliance item:', error);
    throw error;
  }
};

// Update a custom compliance item
export const updateComplianceItem = async (projectId, itemId, itemData) => {
  try {
    const response = await axios.put(`${BASE_URL}/compliance/projects/${projectId}/items/${itemId}`, itemData, {
      headers: getAuthHeaders(),
    });
    return response.data.data;
  } catch (error) {
    console.error('Failed to update compliance item:', error);
    throw error;
  }
};

// Delete a custom compliance item
export const deleteComplianceItem = async (projectId, itemId) => {
  try {
    const response = await axios.delete(`${BASE_URL}/compliance/projects/${projectId}/items/${itemId}`, {
      headers: getAuthHeaders(),
    });
    return response.data.data;
  } catch (error) {
    console.error('Failed to delete compliance item:', error);
    throw error;
  }
};

// Add approval to a compliance item
export const addComplianceApproval = async (projectId, itemId, approvalData) => {
  try {
    const response = await axios.post(`${BASE_URL}/compliance/projects/${projectId}/items/${itemId}/approvals`, approvalData, {
      headers: getAuthHeaders(),
    });
    return response.data.data;
  } catch (error) {
    console.error('Failed to add compliance approval:', error);
    throw error;
  }
};

// Refresh compliance items for a project
export const refreshComplianceItems = async (projectId) => {
  try {
    const response = await axios.post(`${BASE_URL}/compliance/projects/${projectId}/refresh`, {}, {
      headers: getAuthHeaders(),
    });
    return response.data.data;
  } catch (error) {
    console.error('Failed to refresh compliance items:', error);
    throw error;
  }
};

// Get compliance policies
export const getCompliancePolicies = async () => {
  try {
    const response = await axios.get(`${BASE_URL}/compliance/policies`, {
      headers: getAuthHeaders(),
    });
    return response.data.data;
  } catch (error) {
    console.error('Failed to fetch compliance policies:', error);
    throw error;
  }
};

// Create a compliance policy
export const createCompliancePolicy = async (policyData) => {
  try {
    const response = await axios.post(`${BASE_URL}/compliance/policies`, policyData, {
      headers: getAuthHeaders(),
    });
    return response.data.data;
  } catch (error) {
    console.error('Failed to create compliance policy:', error);
    throw error;
  }
};

// Update a compliance policy
export const updateCompliancePolicy = async (policyId, policyData) => {
  try {
    const response = await axios.put(`${BASE_URL}/compliance/policies/${policyId}`, policyData, {
      headers: getAuthHeaders(),
    });
    return response.data.data;
  } catch (error) {
    console.error('Failed to update compliance policy:', error);
    throw error;
  }
};

// Delete a compliance policy
export const deleteCompliancePolicy = async (policyId) => {
  try {
    const response = await axios.delete(`${BASE_URL}/compliance/policies/${policyId}`, {
      headers: getAuthHeaders(),
    });
    return response.data.data;
  } catch (error) {
    console.error('Failed to delete compliance policy:', error);
    throw error;
  }
};

// Get policies applicable to a specific project
export const getApplicablePolicies = async (projectId) => {
  try {
    const response = await axios.get(`${BASE_URL}/compliance/policies/applicable/${projectId}`, {
      headers: getAuthHeaders(),
    });
    return response.data.data;
  } catch (error) {
    console.error('Failed to fetch applicable policies:', error);
    throw error;
  }
};

// Get compliance report for a project
export const getComplianceReport = async (projectId) => {
  try {
    const response = await axios.get(`${BASE_URL}/compliance/projects/${projectId}/report`, {
      headers: getAuthHeaders(),
    });
    return response.data.data;
  } catch (error) {
    console.error('Failed to get compliance report:', error);
    throw error;
  }
};